<template>
  <section
    class="home-testimonials mt-5 pt-5"
    aria-labelledby="home-testimonials-title"
  >
    <HomeInstagramGrid />
    <div class="container px-2 px-lg-4 pt-5">
      <div
        class="home-testimonials__head pt-5"
        data-aos="review-head-reveal"
        data-aos-delay="80"
        data-aos-duration="600"
        data-aos-easing="ease-out-cubic"
      >
        <h2 id="home-testimonials-title" class="home-testimonials__title">
          QUÉ DICEN NUESTROS CLIENTES
        </h2>
        <div
          v-if="totalPages > 1"
          class="home-testimonials__nav home-testimonials__nav--desktop"
          role="group"
          aria-label="Cambiar opiniones"
        >
          <button
            type="button"
            class="home-testimonials__arrow home-testimonials__arrow--prev"
            aria-label="Opiniones anteriores"
            @click="prevPage"
          >
            <i class="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="home-testimonials__arrow home-testimonials__arrow--next"
            aria-label="Opiniones siguientes"
            @click="nextPage"
          >
            <i class="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div v-if="loading" class="home-testimonials__grid home-testimonials__grid--skeleton">
        <div v-for="n in 3" :key="n" class="home-testimonials__skel-card" />
      </div>

      <div v-else class="home-testimonials__grid">
        <article
          v-for="(item, idx) in visibleItems"
          :key="item.key"
          class="home-testimonials__card"
          data-aos="review-card-reveal"
          :data-aos-delay="String(110 + idx * 70)"
          data-aos-duration="560"
          data-aos-easing="ease-out-cubic"
        >
          <p class="home-testimonials__quote">{{ item.quote }}</p>
          <div class="home-testimonials__stars">
            <span class="visually-hidden">{{ item.rating }} de 5 estrellas</span>
            <i
              v-for="star in 5"
              :key="star"
              class="fas fa-star home-testimonials__star"
              :class="{ 'is-on': star <= item.rating }"
              aria-hidden="true"
            />
          </div>
          <div class="home-testimonials__author">
            <div class="home-testimonials__avatar-wrap">
              <img
                v-if="item.avatarUrl"
                :src="item.avatarUrl"
                :alt="''"
                class="home-testimonials__avatar"
                width="44"
                height="44"
                loading="lazy"
              />
              <span v-else class="home-testimonials__avatar-fallback" aria-hidden="true">
                {{ initials(item.authorName) }}
              </span>
            </div>
            <span class="home-testimonials__name">{{ item.authorName }}</span>
          </div>
        </article>
      </div>

      <div
        v-if="totalPages > 1"
        class="home-testimonials__nav home-testimonials__nav--mobile"
        role="group"
        aria-label="Cambiar opiniones"
      >
        <button
          type="button"
          class="home-testimonials__arrow home-testimonials__arrow--prev"
          aria-label="Opiniones anteriores"
          @click="prevPage"
        >
          <i class="fas fa-chevron-left" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="home-testimonials__arrow home-testimonials__arrow--next"
          aria-label="Opiniones siguientes"
          @click="nextPage"
        >
          <i class="fas fa-chevron-right" aria-hidden="true" />
        </button>
      </div>

      
      <div
        class="home-testimonials__brands"
        data-aos="review-head-reveal"
        data-aos-delay="140"
        data-aos-duration="600"
        data-aos-easing="ease-out-cubic"
      >
        <BrandsSection />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import AOS from 'aos'
import reviews from '@/../reviews.json'
import BrandsSection from '@/components/home/BrandsSection.vue'
import HomeInstagramGrid from '@/components/home/HomeInstagramGrid.vue'

const PER_PAGE = 3

const loading = ref(true)
const apiItems = ref([])
const page = ref(0)
const autoRotateId = ref(null)

function normalizeApi(t) {
  return {
    key: `api-${t.id}`,
    quote: t.quote,
    authorName: t.author_name,
    rating: Math.min(5, Math.max(1, Number(t.rating) || 5)),
    avatarUrl: t.avatar_url || null,
    isMock: false,
  }
}

function normalizeReview(r, idx) {
  return {
    key: `review-${idx}`,
    quote: r.opinion,
    authorName: r.nombre,
    rating: Math.min(5, Math.max(1, Number(r.valoracion) || 5)),
    avatarUrl: null,
    isMock: false,
  }
}

const mergedList = computed(() => {
  const fromJson = Array.isArray(reviews) ? reviews.map(normalizeReview) : []
  const fromApi = (apiItems.value || []).map(normalizeApi)
  return [...fromJson, ...fromApi]
})

const totalPages = computed(() =>
  Math.max(1, Math.ceil(mergedList.value.length / PER_PAGE))
)

const visibleItems = computed(() => {
  const list = mergedList.value
  const start = page.value * PER_PAGE
  return list.slice(start, start + PER_PAGE)
})

watch(mergedList, () => {
  if (page.value >= totalPages.value) page.value = Math.max(0, totalPages.value - 1)
})

function prevPage() {
  page.value = (page.value - 1 + totalPages.value) % totalPages.value
  startAutoRotate()
}

function nextPage() {
  page.value = (page.value + 1) % totalPages.value
  startAutoRotate()
}

function stopAutoRotate() {
  if (autoRotateId.value) {
    clearInterval(autoRotateId.value)
    autoRotateId.value = null
  }
}

function startAutoRotate() {
  stopAutoRotate()
  if (totalPages.value <= 1) return
  autoRotateId.value = setInterval(() => {
    page.value = (page.value + 1) % totalPages.value
  }, 3000)
}

async function refreshAos() {
  await nextTick()
  if (typeof AOS.refresh === 'function') {
    AOS.refresh()
  }
}

function initials(name) {
  if (!name) return '?'
  const p = name.trim().split(/\s+/)
  const a = p[0]?.[0] || ''
  const b = p.length > 1 ? p[p.length - 1][0] : ''
  return (a + b).toUpperCase()
}

onMounted(() => {
  apiItems.value = []
  loading.value = false
  startAutoRotate()
  refreshAos()
})

onUnmounted(() => {
  stopAutoRotate()
})

watch(totalPages, () => {
  startAutoRotate()
  refreshAos()
})

watch(page, () => {
  refreshAos()
})
</script>

<style scoped>
.home-testimonials {
  padding: clamp(2rem, 5vw, 3rem) 0 0;
  background: #ffffff;
  color: #111111;
  min-height: 60vh;
}

.home-testimonials :deep(.container) {
  padding-left: max(0.5rem, env(safe-area-inset-left, 0px));
  padding-right: max(0.5rem, env(safe-area-inset-right, 0px));
}

.home-testimonials__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
}

[data-aos="review-head-reveal"],
[data-aos="review-card-reveal"] {
  opacity: 0;
  transform: translate3d(0, 16px, 0);
  transition-property: opacity, transform;
}

[data-aos="review-card-reveal"] {
  transform: translate3d(0, 18px, 0) scale(0.985);
}

[data-aos="review-head-reveal"].aos-animate,
[data-aos="review-card-reveal"].aos-animate {
  opacity: 1;
  transform: translate3d(0, 0, 0) scale(1);
}

.home-testimonials__title {
  margin: 0;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #111111;
}

.home-testimonials__nav {
  display: flex;
  gap: 0.5rem;
}

.home-testimonials__nav--mobile {
  display: none;
}

.home-testimonials__arrow {
  position: relative;
  box-sizing: border-box;
  min-width: 2.75rem;
  min-height: 2.75rem;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #dddddd;
  background: #ffffff;
  color: #333333;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.home-testimonials__arrow:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.home-testimonials__arrow:hover {
  border-color: #bdbdbd;
  color: #111111;
}

.home-testimonials__arrow--next {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.home-testimonials__arrow--next:hover {
  background: var(--primary-dark);
  border-color: var(--primary-dark);
  color: #fff;
}

.home-testimonials__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  align-items: stretch;
}

.home-testimonials__card {
  border-radius: 16px;
  padding: 1.35rem 1.4rem 1.25rem;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #ffffff;
  border: 1px solid #e6e6e6;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
}

.home-testimonials__card:hover {
  border-color: #d2d2d2;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.home-testimonials__quote {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #3a3a3a;
  flex: 1 1 auto;
}

.home-testimonials__stars {
  display: flex;
  gap: 0.2rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.home-testimonials__star {
  color: rgba(17, 17, 17, 0.2);
}

.home-testimonials__star.is-on {
  color: #facc15;
}

.home-testimonials__author {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.home-testimonials__avatar-wrap {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid var(--primary-color);
  background: #f3f3f3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-testimonials__avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.home-testimonials__avatar-fallback {
  font-size: 0.85rem;
  font-weight: 800;
  color: #6b6b6b;
}

.home-testimonials__name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #111111;
  overflow-wrap: anywhere;
}

.home-testimonials__grid--skeleton {
  min-height: 200px;
}

.home-testimonials__skel-card {
  border-radius: 16px;
  min-height: 220px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: home-t-skel 1.1s ease infinite;
}

.home-testimonials__brands {
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
}

.home-testimonials__brands :deep(.home-brands) {
  background: #ffffff;
}

.home-testimonials__brands :deep(.home-brands__inner) {
  border-top: 1px solid #e6e6e6;
  border-bottom: 1px solid #e6e6e6;
}

.home-testimonials__brands :deep(.home-brands__fog--left) {
  background: linear-gradient(to right, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
}

.home-testimonials__brands :deep(.home-brands__fog--right) {
  background: linear-gradient(to left, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
}

.home-testimonials__brands :deep(.home-brands__logo) {
  filter: brightness(0) saturate(100%);
  opacity: 0.75;
}

@keyframes home-t-skel {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 991.98px) {
  .home-testimonials__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .home-testimonials__head {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

@media (max-width: 767.98px) {
  .home-testimonials__grid {
    grid-template-columns: 1fr;
  }

  .home-testimonials__head {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
  }

  .home-testimonials__title {
    text-align: center;
  }

  .home-testimonials__nav {
    align-self: center;
  }

  .home-testimonials__nav--desktop {
    display: none;
  }

  .home-testimonials__nav--mobile {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }
}

@media (max-width: 379.98px) {
  .home-testimonials__card {
    padding: 1.15rem 1.15rem 1.1rem;
  }
}
</style>
